/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import {
  FlexContainer,
  Spinner,
  SVGIcon,
  SVGIconProps,
  Text,
  theme,
} from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { DASHBOARD_MOBILE_BREAKPOINT } from "../styles";

type Props = {
  icon: SVGIconProps["name"];
  label: string;
  count: number | undefined;
  link?: string;
  loading?: boolean;
  layout?: "row" | "column" | "responsive-desktop-column";
};

export function StatItem({
  icon,
  label,
  count,
  link,
  loading = false,
  layout = "row",
}: Props) {
  const isDesktopVertical = layout !== "row";
  const isMobileVertical = layout === "column";

  return (
    <FlexContainer
      css={{
        gap: theme.spacing.spacing02,
        ...getContainerAxisStyle(isDesktopVertical),
        [`@media (max-width: ${DASHBOARD_MOBILE_BREAKPOINT}px)`]: {
          ...getContainerAxisStyle(isMobileVertical),
        },
      }}
    >
      <If condition={isNotNil(link)}>
        <Then>
          <Link to={link ?? "."}>
            <FlexContainer gap={theme.spacing.spacing02}>
              <SVGIcon
                name={icon}
                css={{ fill: theme.color.interactive.link }}
              />
              <Text
                mb={0}
                css={{ textWrap: "nowrap" }}
                color={theme.color.interactive.link}
              >
                {label}
              </Text>
            </FlexContainer>
          </Link>
        </Then>
        <Else>
          <FlexContainer gap={theme.spacing.spacing02}>
            <SVGIcon name={icon} />
            <Text mb={0} css={{ textWrap: "nowrap" }}>
              {label}
            </Text>
          </FlexContainer>
        </Else>
      </If>

      {/* margin-left to vertical align with text above = icon size (1.5rem) + gap (0.5rem) */}
      <div
        css={
          isDesktopVertical
            ? { marginLeft: theme.spacing.spacing07 }
            : {
                minWidth: "3rem", // reserved width for 4 digital number
                display: "flex",
                justifyContent: "flex-end", // align spinner and number to the right
                marginRight: theme.spacing.spacing03,
                [`@media (max-width: ${DASHBOARD_MOBILE_BREAKPOINT}px)`]: {
                  marginRight: 0,
                },
              }
        }
      >
        <If condition={loading}>
          <Then>
            <Spinner />
          </Then>
          <Else>
            <If condition={isNotNil(count)}>
              <Then>
                <Text
                  size="lg"
                  weight="bold"
                  lineHeight="1.5rem" // Same height as spinner
                  mb={0}
                >
                  {count}
                </Text>
              </Then>
            </If>
          </Else>
        </If>
      </div>
    </FlexContainer>
  );
}

const getContainerAxisStyle = (isVertical: boolean) =>
  ({
    flexDirection: isVertical ? "column" : "row",
    alignItems: isVertical ? "flex-start" : "center",
    justifyContent: isVertical ? "flex-start" : "space-between",
  } as const);
